import React, { createContext, useMemo } from 'react';

import { Field, type FieldRenderProps } from 'react-final-form';
import { uid } from 'react-uid';

import { Label } from '@atlaskit/form';
import { Inline } from '@atlaskit/primitives';

import RenderChildren from '../render-children';

import { HintPopup } from './hint-popup';
import { Container, Required } from './styled';
import type { EocFieldProps, EocFieldRenderProps } from './types';

export const FieldId = createContext<string | undefined>(undefined);

export const EocField = <Type extends any = string>({
	id,
	children,
	hint,
	isRequired,
	label,
	name,
	helperMessageComponent,
	...restFieldProps
}: EocFieldProps<Type>) => {
	const fieldId = useMemo(() => (id ? id : `${name}-${uid({ id: name })}`), [id, name]);

	return (
		<Field name={name} {...restFieldProps}>
			{(fieldRenderProps: FieldRenderProps<Type>) => (
				<Container>
					{label && (
						<Inline alignBlock="baseline">
							<Label id={`${fieldId}-label`} htmlFor={fieldId}>
								{label}
								{isRequired && <Required />}
							</Label>
							{hint && <HintPopup hint={hint} label={label} />}
							{helperMessageComponent}
						</Inline>
					)}
					<FieldId.Provider value={fieldId}>
						{RenderChildren<EocFieldRenderProps<Type>>(children, {
							input: {
								id: fieldId,
								isInvalid: fieldRenderProps.meta.touched && !fieldRenderProps.meta.valid,
								'aria-invalid': !!fieldRenderProps.meta.error ? true : undefined,
								'aria-labelledby': `${fieldId}-label`,
								'aria-describedby': `${fieldId}-helper ${fieldId}-valid ${fieldId}-error`,
								'aria-required': isRequired,
								...fieldRenderProps.input,
							},
							meta: fieldRenderProps.meta,
						})}
					</FieldId.Provider>
				</Container>
			)}
		</Field>
	);
};
